import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { BRAND, COLORS } from "../../lib/brand";
import { LayoutDashboard, ClipboardList, CheckSquare, DollarSign, Users, CalendarCheck, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/admin/orders", icon: ClipboardList, label: "Apostille Orders", testid: "nav-orders" },
  { to: "/admin/bookings", icon: CalendarCheck, label: "Service Bookings", testid: "nav-bookings" },
  { to: "/admin/notaries", icon: Users, label: "Notary Network", testid: "nav-notaries" },
  { to: "/admin/tasks", icon: CheckSquare, label: "Task Tracker", testid: "nav-tasks" },
  { to: "/admin/finance", icon: DollarSign, label: "Finance", testid: "nav-finance" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-[#FBF8F1]" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 bg-[#4A0000] text-white">
        <div className="flex items-center gap-2">
          <img src={BRAND.logoUrl} alt="" className="w-8 h-8 rounded-full" />
          <span className="font-medium">Command Center</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="admin-mobile-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen w-64 z-30
          flex flex-col text-white
          transform transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        style={{ background: COLORS.maroonDeep }}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="" className="w-10 h-10 rounded-full border border-[#D4AF37]" />
          <div className="leading-tight">
            <div className="font-medium text-sm">India's Inking</div>
            <div className="text-[11px] text-white/60">Command Center</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMobileOpen(false)}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
                  isActive ? "bg-white/10 text-[#D4AF37]" : "text-white/80 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-white/60">
            Signed in as
            <div className="text-white truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/80 hover:bg-white/5 hover:text-white transition"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            Log Out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-20" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
